import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CubMascot from './CubMascot.jsx';

/**
 * The little help cub in the bottom-right corner.
 *
 * Deliberately NOT an AI. It is a curated set of answers about CyberCub,
 * matched on keywords, running entirely in the browser. Nothing a child
 * types is sent anywhere - which on a children's site is the whole
 * point - and anything it does not know it hands to the Contact page,
 * where a real person is.
 *
 * Adding an answer is just another entry in KB: some keywords, a reply,
 * and optionally a link. Keep replies short and route anything that
 * could drift (prices, exact figures) to the page that owns the truth
 * rather than repeating it here.
 */

/* Each entry: keywords to match, the reply, and an optional in-app link.
   Order matters only as a tie-break — the highest keyword score wins. */
const KB = [
  {
    id: 'login',
    keywords: ['log in', 'login', 'sign in', 'signin', 'cannot get in', 'can\'t get in', 'access my account'],
    reply: 'Sign in with the email you signed up with — or, if your school gave you the account, the User ID your teacher gave you.',
    link: { to: '/login', label: 'Go to sign in' },
  },
  {
    id: 'forgot',
    keywords: ['forgot', 'forgotten', 'reset', 'reset password', 'reset my password', 'lost password', 'cannot remember', 'can\'t remember', 'wrong password', 'locked out', 'locked'],
    reply: 'If your school set up your account, your teacher can reset the password fastest. If you signed up yourself, send us the email you used and we will help.',
    link: { to: '/contact', label: 'Contact us' },
  },
  {
    id: 'signup',
    keywords: ['sign up', 'signup', 'register', 'create account', 'new account', 'join', 'how do i start'],
    reply: 'CyberCub accounts are set up by your school or teacher — they give you a User ID and password to sign in with. There is no public sign-up.',
    link: { to: '/login', label: 'Go to sign in' },
  },
  {
    id: 'courses',
    keywords: ['course', 'courses', 'learn', 'lessons', 'modules', 'what can i learn', 'topics'],
    reply: 'There are two courses: Cyber Smart Kids, which starts at age 7 and covers staying safe online, and Ethical Hacker, from age 11, which goes underneath — how computers, data and networks really work.',
    link: { to: '/#courses', label: 'See the courses' },
  },
  {
    id: 'price',
    keywords: ['price', 'cost', 'how much', 'pay', 'payment', 'fee', 'subscription', 'money', 'expensive', 'free'],
    reply: 'Each course is a monthly subscription. You will see the exact price on the home page and again at checkout, before anything is paid.',
    link: { to: '/#courses', label: 'See pricing' },
  },
  {
    id: 'leaderboard',
    keywords: ['leaderboard', 'rank', 'ranking', 'top', 'prize', 'prizes', 'reward', 'competition', 'winner'],
    reply: 'The leaderboard ranks cubs by points, earned from finishing lessons and passing quizzes. The top three win prizes, handed out at the school’s annual function.',
    link: { to: '/leaderboard', label: 'Open the leaderboard' },
  },
  {
    id: 'badges',
    keywords: ['badge', 'badges', 'xp', 'points', 'trophy', 'trophies', 'earn'],
    reply: 'You earn XP and badges by finishing lessons and passing their quizzes. Every badge you have collected shows up on your profile and your badge shelf.',
    link: { to: '/learn/badges', label: 'My badges' },
  },
  {
    id: 'quests',
    keywords: ['quest', 'quests', 'cub quest', 'to do', 'todo', 'mission', 'missions', 'checklist'],
    reply: 'Cub Quests are little real-world things to go and do for each module — like finding the padlock on a website, or checking a device has a lock screen. Tick them off on the course page as you do them.',
    link: { to: '/learn', label: 'My courses' },
  },
  {
    id: 'profile',
    keywords: ['profile', 'my details', 'my account', 'my stats', 'change cub', 'avatar', 'change password'],
    reply: 'Your profile shows who you are and how far you have come — your XP, lessons, badges and rank. You can change your cub and your password from there too.',
    link: { to: '/learn/me', label: 'My profile' },
  },
  {
    id: 'practical',
    keywords: ['practical', 'practicals', 'lab', 'labs', 'activity', 'game', 'hands on', 'exercise'],
    reply: 'Most lessons end with a practical that opens in its own tab. Work through it to the last screen and the lesson unlocks the next step on its own — you can keep the lesson open beside it.',
  },
  {
    id: 'age',
    keywords: ['age', 'old', 'how old', 'years', 'suitable', 'kids', 'children', 'child'],
    reply: 'Cyber Smart Kids is built for ages 7 and up; Ethical Hacker is for 11 and up. Accounts can be for anyone from 5 to 51.',
  },
  {
    id: 'contact',
    keywords: ['contact', 'help', 'support', 'talk to someone', 'email you', 'reach you', 'problem', 'question'],
    reply: 'The Contact page reaches a real person — tell us what happened and which account, and we will come back to you.',
    link: { to: '/contact', label: 'Contact us' },
  },
  {
    id: 'safety',
    keywords: ['safe', 'safety', 'data', 'privacy', 'parent', 'private', 'collect', 'information'],
    reply: 'CyberCub keeps very little: a name, an age, a parent’s mobile number and the email used to sign in. No adverts, no messaging between users, and nothing sold. The About page explains it all.',
    link: { to: '/about', label: 'About CyberCub' },
  },
  {
    id: 'school',
    keywords: ['school', 'teacher', 'class', 'bring to school', 'for schools', 'classroom'],
    reply: 'Schools are welcome — choose "Bringing CyberCub to a school" on the Contact page and tell us roughly how many children and which year groups.',
    link: { to: '/contact', label: 'Ask about schools' },
  },

  /* ---- cybersecurity & networking, the subjects the courses teach ----
     Kept simple and age-appropriate, and firmly on the recognise-don't-
     perform side: these explain what a thing is, never how to carry an
     attack out. */
  {
    id: 'cybersecurity',
    keywords: ['cybersecurity', 'cyber security', 'what is cyber', 'online safety', 'stay safe online', 'infosec'],
    reply: 'Cybersecurity is keeping people and their information safe online. It really asks three questions of anything: is it secret, has it been changed, and is it truly from who it claims? Almost everything else is a detail of those three.',
  },
  {
    id: 'phishing',
    keywords: ['phishing', 'phish', 'fake email', 'scam email', 'fake message', 'trick email', 'suspicious link'],
    reply: 'Phishing is a fake message — an email or text pretending to be someone you trust — to trick you into clicking a link or giving away a password. The tells are always the same: it rushes you, sounds too good, or asks you to keep a secret.',
  },
  {
    id: 'malware',
    keywords: ['malware', 'virus', 'viruses', 'trojan', 'worm', 'spyware', 'ransomware'],
    reply: 'Malware is any program built to do harm — a virus spreads, spyware watches, ransomware locks your files for money. The best defences are dull and effective: keep software updated, and never run things from people you do not trust.',
  },
  {
    id: 'firewall',
    keywords: ['firewall'],
    reply: 'A firewall is a guard between your device or network and the outside. It decides which connections are allowed in or out and blocks the rest — and blocking what you do not need is one of the quietest, strongest defences there is.',
  },
  {
    id: 'strong-password',
    keywords: ['strong password', 'good password', 'secure password', 'make a password', 'best password', 'password strong'],
    reply: 'A strong password is long and unusual. Three random words stuck together — like PurpleGoatBanana — beat one short tricky word, because length is what makes a password hard to guess. And never reuse the same one across accounts.',
  },
  {
    id: 'twofactor',
    keywords: ['two factor', '2fa', 'two step', '2 step', 'second factor', 'authenticator', 'one time code', 'otp'],
    reply: 'Two-step verification adds a second check after your password — usually a code from an app or a text. Even if someone steals the password, they still cannot get in without that second thing. Never read a one-time code out to anyone.',
  },
  {
    id: 'https',
    keywords: ['https', 'padlock', 'ssl', 'tls', 'the lock', 'secure connection', 'certificate'],
    reply: 'The padlock (HTTPS) means the connection to a site is private and scrambled, so nobody in between can read it, and that the site really owns its name. It does not promise the site is honest — only that the line is secure.',
  },
  {
    id: 'encryption',
    keywords: ['encryption', 'encrypt', 'encrypted', 'cipher', 'scramble'],
    reply: 'Encryption scrambles a message so only someone with the right key can read it. The clever part is that the method is public and only the key is secret — so everyone can safely use the same trusted method.',
  },
  {
    id: 'hashing',
    keywords: ['hash', 'hashing', 'sha', 'bcrypt', 'fingerprint'],
    reply: 'A hash is a one-way fingerprint of some data: same input, same fingerprint, and no way back. It is how a site can check your password without ever storing it. A hash is not encryption — there is no key and nothing to unscramble.',
  },
  {
    id: 'encoding',
    keywords: ['base64', 'encoding', 'encode', 'encoded'],
    reply: 'Encoding (like Base64) just rewrites data into another shape — anyone can undo it, and there is no key. So encoding is not protection: a password stored as Base64 is basically plain text with extra steps.',
  },
  {
    id: 'vpn',
    keywords: ['vpn'],
    reply: 'A VPN builds a private, encrypted tunnel from your device to a server elsewhere, so your internet provider cannot see what you visit and sites see the server instead of you. It hides the path — it does not make you invisible or safe from everything.',
  },
  {
    id: 'ip',
    keywords: ['ip address', 'ip addresses'],
    reply: 'An IP address is the number that says where a device is on a network — a postal address for computers. Every packet carries one so the reply knows where to come back to.',
  },
  {
    id: 'mac',
    keywords: ['mac address'],
    reply: 'A MAC address is the fixed name of one network card — which device you are, rather than where you are. An IP address can change; a MAC address is baked into the hardware.',
  },
  {
    id: 'dns',
    keywords: ['dns', 'domain name', 'name resolution', 'lookalike domain'],
    reply: 'DNS is the internet’s address book: it turns a name like cybercub.org into the number a computer actually connects to. No single machine holds the whole list — a lookup is a chain of questions to different servers.',
  },
  {
    id: 'packet',
    keywords: ['packet', 'packets'],
    reply: 'Nothing crosses the internet in one piece. Your message is cut into little packets, each with an address, sent separately, and reassembled at the other end.',
  },
  {
    id: 'network',
    keywords: ['router', 'switch', 'network', 'networking', 'lan', 'wifi', 'wi-fi'],
    reply: 'A network is just machines that can send each other things. A switch joins machines into one network; a router joins different networks together. Everything bigger — including the whole internet — is that idea repeated.',
  },
  {
    id: 'port',
    keywords: ['port', 'ports', 'open port'],
    reply: 'A port is a numbered door on a machine — one for web pages, one for email, and so on — so many kinds of traffic can share one address. An open port nobody is watching is a classic way in, which is why closing the ones you do not need matters.',
  },
  {
    id: 'ethical-hacker-term',
    keywords: ['ethical hacker', 'ethical hacking', 'white hat', 'hacker', 'hacking', 'penetration test', 'pentest'],
    reply: 'An ethical hacker looks for weaknesses the same way an attacker would — but with permission, and to get them fixed. The whole difference is permission and intent: finding the open window so it can be shut, not to climb through.',
  },
  {
    id: 'vulnerability',
    keywords: ['vulnerability', 'cve', 'exploit', 'zero day', 'patch'],
    reply: 'A vulnerability is a mistake in software that could be abused; an exploit is the trick that abuses it; a CVE is the world’s shared ID for one particular vulnerability. A patch is the fix — installing updates is how you close them.',
  },
  {
    id: 'social-engineering',
    keywords: ['social engineering', 'manipulation', 'pretexting', 'trick people', 'con'],
    reply: 'Social engineering is hacking the person, not the computer — talking someone into opening a door, sharing a password, or rushing a decision. It is often cheaper than breaking any technology, which is why staying calm and checking is a real defence.',
  },
  {
    id: 'cookie',
    keywords: ['cookie', 'cookies'],
    reply: 'The web forgets you between clicks, so a cookie is a little note your browser carries back each time — that is how a site keeps you logged in. Some cookies also track what you look at, which is why "decline non-essential" is a good habit.',
  },
  {
    id: 'web-attacks',
    keywords: ['sql injection', 'injection', 'xss', 'web hacking', 'cross site'],
    reply: 'These happen when a website trusts something a visitor typed that it should have checked. The browser is the visitor’s own machine, so the only checks that count happen on the server — that one idea sits behind most web weaknesses.',
  },
  {
    id: 'ddos',
    keywords: ['ddos', 'denial of service', 'botnet'],
    reply: 'A denial-of-service attack floods a site with so much fake traffic that real visitors cannot get through, and a botnet is a crowd of hijacked computers used to do it. The defence is spotting and soaking up the flood before it lands.',
  },
  {
    id: 'personal-data',
    keywords: ['personal data', 'personal information', 'private information', 'digital footprint', 'pii'],
    reply: 'Personal data is anything that points to you — your name, birthday, school, photos, where you are. The trail you leave is your digital footprint, and it lasts. The rule of thumb: share the least that still gets the job done.',
  },
  {
    id: 'otp-scam',
    keywords: ['otp scam', 'bank scam', 'upi scam', 'fraud call', 'lottery', 'prize scam', 'fake call'],
    reply: 'No real bank, company or official ever phones to ask for your password, PIN or one-time code — that request is the scam, every time. Hang up, and if unsure call the number printed on the back of the card yourself.',
  },
];

const GREETING = "Hi! I’m the CyberCub helper. Ask me about the site — logging in, courses, the leaderboard — or about cybersecurity and networking, like “what is phishing?” or “what is a firewall?”";

/* The chips shown under the greeting, by KB id — a mix of site help and
   a couple of subject questions, so it is clear the bot does both. */
const SUGGESTIONS = ['login', 'courses', 'phishing', 'strong-password'];
const SUGGESTION_LABEL = {
  login: 'How do I log in?',
  courses: 'What courses are there?',
  phishing: 'What is phishing?',
  'strong-password': 'What makes a strong password?',
};

const FALLBACK = {
  reply: "I’m only a little help cub, so I might not know that one yet. The courses go far deeper into cybersecurity and networking — and for anything else, the Contact page reaches a real person.",
  link: { to: '/contact', label: 'Contact us' },
};

/**
 * Score how well a query matches an entry: a keyword found in the query
 * scores its length (so "leaderboard" beats a stray "the"), and longer,
 * more specific phrases naturally win over single loose words.
 */
function matchKB(query) {
  const q = ` ${query.toLowerCase().replace(/[^a-z0-9'\s]/g, ' ').replace(/\s+/g, ' ')} `;
  let best = null;
  let bestScore = 0;
  for (const entry of KB) {
    let score = 0;
    for (const kw of entry.keywords) {
      if (q.includes(` ${kw} `) || q.includes(`${kw} `) || q.includes(` ${kw}`)) {
        score += kw.length;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }
  // A single short accidental hit is not a real match.
  return bestScore >= 3 ? best : null;
}

export default function HelpBot() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{ from: 'bot', text: GREETING }]);
  const [showChips, setShowChips] = useState(true);

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  // New messages scroll into view; opening focuses the input.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Esc closes it, wherever focus is inside the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const answer = (text) => {
    const clean = text.trim();
    if (!clean) return;
    const match = matchKB(clean);
    const found = match ?? FALLBACK;
    setShowChips(false);
    setMessages((m) => [
      ...m,
      { from: 'me', text: clean },
      { from: 'bot', text: found.reply, link: found.link ?? null },
    ]);
    setInput('');
  };

  const onSubmit = (e) => {
    e.preventDefault();
    answer(input);
  };

  // The practical and explainer pages run full-bleed and interactive; a
  // floating helper there overlaps their controls, so it stays out of
  // them. Placed after the hooks so their order never changes.
  if (pathname.startsWith('/lab/') || pathname.startsWith('/watch/')) return null;

  return (
    <div className="helpbot">
      {open && (
        <div
          className="helpbot-panel"
          role="dialog"
          aria-label="CyberCub helper"
          ref={panelRef}
        >
          <header className="helpbot-head">
            <span className="helpbot-head-cub">
              <CubMascot avatar="cub-sun" mood="star" size={34} />
            </span>
            <div className="helpbot-head-text">
              <strong>CyberCub helper</strong>
              <span className="helpbot-head-sub">Quick answers about the site</span>
            </div>
            <button
              type="button"
              className="helpbot-close"
              onClick={() => setOpen(false)}
              aria-label="Close the helper"
            >
              ✕
            </button>
          </header>

          <div className="helpbot-body" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i} className={`helpbot-msg is-${m.from}`}>
                <p>{m.text}</p>
                {m.link && (
                  <Link to={m.link.to} className="helpbot-link" onClick={() => setOpen(false)}>
                    {m.link.label} →
                  </Link>
                )}
              </div>
            ))}

            {showChips && (
              <div className="helpbot-chips">
                {SUGGESTIONS.map((id) => (
                  <button key={id} type="button" className="helpbot-chip" onClick={() => answer(SUGGESTION_LABEL[id])}>
                    {SUGGESTION_LABEL[id]}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form className="helpbot-input" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a question…"
              aria-label="Ask the helper a question"
              maxLength={200}
            />
            <button type="submit" className="helpbot-send" disabled={!input.trim()} aria-label="Send">
              ➤
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        className={`helpbot-toggle ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Close the helper' : 'Open the help cub'}
      >
        {open ? (
          <span className="helpbot-toggle-x" aria-hidden="true">✕</span>
        ) : (
          <>
            <CubMascot avatar="cub-sun" mood="wave" size={40} />
            <span className="helpbot-toggle-label">Help</span>
          </>
        )}
      </button>
    </div>
  );
}
